import React, { useState, useContext, useEffect, useRef } from 'react';
import { Link as RouterLink, useHistory, useLocation } from 'react-router-dom';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  makeStyles,
  InputBase,
  Box,
} from '@material-ui/core';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import SearchIcon from '@material-ui/icons/Search';
import { Badge } from '@material-ui/core';
import clsx from 'clsx';

import { Can, check } from '../Can';
import { AuthContext } from '../../context/Auth/AuthContext';
import { WhatsAppsContext } from '../../context/WhatsApp/WhatsAppsContext';
import { i18n } from '../../translate/i18n';
import { menuGroupsConfig } from '../../utils/menuGroups';
import usePlans from '../../hooks/usePlans';
import api from '../../services/api';
import { FaRocket } from 'react-icons/fa';
import useGettingStarted, {
  GETTING_STARTED_EVENT,
  readGettingStartedBadge,
} from '../../hooks/useGettingStarted';

const useStyles = makeStyles((theme) => ({
  searchWrap: {
    display: "flex",
    alignItems: "center",
    margin: "8px 10px 10px",
    padding: "6px 10px",
    borderRadius: 999,
    backgroundColor: theme.mode === "dark" ? "#1f2937" : "#f1f3f9",
    minWidth: 0,
  },
  searchIcon: {
    fontSize: 18,
    color: theme.mode === "dark" ? "#9ca3af" : "#94a3b8",
    marginRight: 6,
    flexShrink: 0,
  },
  searchInput: {
    flex: 1,
    fontSize: 13,
    color: theme.palette.text.primary,
    "& input": {
      padding: "2px 0",
      fontSize: 13,
    },
  },
  searchKbd: {
    fontSize: 10,
    fontWeight: 700,
    color: theme.mode === "dark" ? "#9ca3af" : "#94a3b8",
    backgroundColor: theme.mode === "dark" ? "#111827" : "#fff",
    border: `1px solid ${theme.mode === "dark" ? "#374151" : "#e5e7eb"}`,
    borderRadius: 6,
    padding: "1px 5px",
    flexShrink: 0,
  },
  searchClear: {
    border: 0,
    background: "transparent",
    color: theme.mode === "dark" ? "#9ca3af" : "#64748b",
    fontSize: 16,
    lineHeight: 1,
    cursor: "pointer",
    padding: "0 2px",
    flexShrink: 0,
  },
  searchEmpty: {
    margin: "4px 16px 12px",
    fontSize: 12,
    color: theme.mode === "dark" ? "#9ca3af" : "#64748b",
  },
  groupHeaderActive: {
    backgroundColor: theme.mode === 'dark' ? 'rgba(129, 140, 248, 0.16)' : '#eef0ff',
    color: theme.mode === 'dark' ? '#c7d2fe' : '#4F46F5',
    boxShadow: 'none',
    '&:before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 8,
      bottom: 8,
      width: 3,
      borderRadius: 999,
      backgroundColor: theme.palette.primary.main,
    },
  },
  groupHeaderCollapsed: {
    margin: '4px 0',
    paddingLeft: '0 !important',
    paddingRight: '0 !important',
    justifyContent: 'center',
    minHeight: 44,
    '&:before': {
      display: 'none',
    },
    '& .MuiListItemIcon-root': {
      marginRight: '0 !important',
      minWidth: '28px !important',
      width: 28,
    },
  },
  groupTitle: {
    fontSize: 13.5,
    fontWeight: 800,
    letterSpacing: 0.2,
    color: 'inherit',
    display: 'block',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  groupIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    height: 24,
    flexShrink: 0,
    color: "inherit",
    lineHeight: 0,
    "& svg": {
      fontSize: 22,
    },
  },
  groupHeader: {
    borderRadius: 10,
    margin: '1px 8px',
    minHeight: 40,
    alignItems: 'center',
    overflow: 'hidden',
    position: 'relative',
    backgroundColor: 'transparent',
    color: theme.mode === 'dark' ? '#cbd5e1' : '#475569',
    transition: 'background-color 120ms ease, color 120ms ease',
    '& .MuiListItemIcon-root': {
      minWidth: 36,
      width: 36,
      height: 24,
      marginRight: 6,
      justifyContent: 'center',
      alignItems: 'center',
      display: 'flex',
      flexShrink: 0,
      color: 'inherit',
    },
    '& .MuiListItemText-root': {
      minWidth: 0,
      overflow: 'hidden',
      marginRight: 8,
    },
    '&:hover': {
      backgroundColor: theme.mode === 'dark' ? 'rgba(129, 140, 248, 0.10)' : '#f4f6fb',
      color: theme.mode === 'dark' ? '#ffffff' : '#4F46F5',
    },
  },
  listItem: {
    borderRadius: 10,
    margin: '1px 8px',
    backgroundColor: 'transparent !important',
    color: `${theme.palette.text.primary} !important`,
    '& .MuiListItemText-primary': {
      color: 'inherit !important',
    },
    '& .MuiListItemIcon-root': {
      color: 'inherit !important',
      minWidth: '36px',
      justifyContent: 'center',
      '& svg': {
        fontSize: '20px',
      },
    },
    '&:hover': {
      backgroundColor: `${theme.mode === 'dark' ? 'rgba(129, 140, 248, 0.10)' : '#f4f6fb'} !important`,
    },
  },
  nestedItem: {
    paddingLeft: theme.spacing(2),
    margin: '1px 8px 1px 8px',
    borderRadius: 10,
    minHeight: 38,
    alignItems: 'center',
    overflow: 'hidden',
    position: 'relative',
    backgroundColor: 'transparent !important',
    color: `${theme.mode === 'dark' ? '#cbd5e1' : '#475569'} !important`,
    '& .MuiListItemText-root': {
      minWidth: 0,
      overflow: 'hidden',
    },
    '& .MuiListItemText-primary': {
      color: 'inherit !important',
      fontSize: 12.5,
      fontWeight: 500,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    '& .MuiListItemIcon-root': {
      color: 'inherit !important',
      minWidth: 28,
      width: 28,
      height: 22,
      marginRight: 8,
      justifyContent: 'center',
      alignItems: 'center',
      display: 'flex',
      flexShrink: 0,
      '& svg': {
        fontSize: 18,
      },
    },
    '&:hover': {
      backgroundColor: `${theme.mode === 'dark' ? 'rgba(129, 140, 248, 0.10)' : '#f4f6fb'} !important`,
      color: `${theme.mode === 'dark' ? '#ffffff' : '#4F46F5'} !important`,
    },
    '&.Mui-selected': {
      backgroundColor: `${theme.mode === 'dark' ? 'rgba(129, 140, 248, 0.16)' : '#eef0ff'} !important`,
      color: `${theme.mode === 'dark' ? '#c7d2fe' : '#4F46F5'} !important`,
      '& .MuiListItemText-primary': {
        fontWeight: 700,
      },
      '&:before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 8,
        bottom: 8,
        width: 3,
        borderRadius: 999,
        backgroundColor: theme.palette.primary.main,
      },
    },
  },
  nestedItemCollapsed: {
    margin: '2px 0',
    paddingLeft: '0 !important',
    paddingRight: '0 !important',
    justifyContent: 'center',
    '&:before': {
      display: 'none',
    },
    '& .MuiListItemIcon-root': {
      marginRight: '0 !important',
      minWidth: '28px !important',
    },
  },
  expandIcon: {
    color: 'inherit',
    transition: 'transform 160ms ease',
    fontSize: 20,
    flexShrink: 0,
  },
  expandedIcon: {
    transform: 'rotate(180deg)',
  },
  firstStepsItem: {
    borderRadius: 10,
    margin: '1px 8px 4px',
    minHeight: 40,
    position: 'relative',
    overflow: 'hidden',
    color: theme.mode === 'dark' ? '#cbd5e1' : '#475569',
    '& .MuiListItemIcon-root': {
      minWidth: 36,
      width: 36,
      justifyContent: 'center',
      color: 'inherit',
    },
    '& .MuiListItemText-primary': {
      fontSize: 14,
      fontWeight: 600,
      color: 'inherit',
    },
    '&:hover': {
      backgroundColor: theme.mode === 'dark' ? 'rgba(129, 140, 248, 0.10)' : '#f4f6fb',
      color: theme.mode === 'dark' ? '#ffffff' : '#4F46F5',
    },
    '&.Mui-selected': {
      backgroundColor: theme.mode === 'dark' ? 'rgba(129, 140, 248, 0.16)' : '#eef0ff',
      color: theme.mode === 'dark' ? '#c7d2fe' : '#4F46F5',
      '&:before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 8,
        bottom: 8,
        width: 3,
        borderRadius: 999,
        backgroundColor: theme.palette.primary.main,
      },
    },
  },
  menuSectionDivider: {
    height: 1,
    margin: "8px 16px 10px",
    backgroundColor: theme.mode === "dark" ? "#334155" : "#e5e8f0",
    border: "none",
    flexShrink: 0,
  },
  nestedSectionLabel: {
    display: "block",
    padding: "10px 12px 4px 14px",
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: theme.mode === "dark" ? "#94a3b8" : "#94a3b8",
  },
  groupHeaderExpanded: {
    backgroundColor: theme.mode === "dark" ? "rgba(148, 163, 184, 0.10)" : "#f4f6fb",
  },
  groupItemsWrap: {
    margin: "0 10px 8px 18px",
    padding: "4px 0 4px 6px",
    borderLeft: `2px solid ${theme.mode === "dark" ? "#334155" : "#e5e8f0"}`,
    borderRadius: "0 10px 10px 0",
    backgroundColor: theme.mode === "dark" ? "rgba(15, 23, 42, 0.28)" : "#f8fafc",
  },
  stepsBadge: {
    flexShrink: 0,
    fontSize: 11,
    fontWeight: 800,
    lineHeight: 1,
    color: theme.palette.primary.main,
    backgroundColor: theme.mode === 'dark' ? 'rgba(129,140,248,0.18)' : '#eef0ff',
    borderRadius: 999,
    padding: '4px 8px',
  },
}));

// ✅ COMPONENTE DE ELEMENTO DE MENÚ (REUTILIZADO)
function ListItemLink({ icon, primary, to, className, badge, drawerOpen = true, classes, ...props }) {
  const renderLink = React.useMemo(
    () =>
      React.forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
      )),
    [to]
  );

  return (
    <li>
      <ListItem 
        button 
        dense 
        component={renderLink} 
        className={clsx(className, !drawerOpen && classes?.nestedItemCollapsed)} 
        style={!drawerOpen ? {
          justifyContent: 'center',
          paddingLeft: '4px',
          paddingRight: '4px',
        } : undefined}
        {...props}
      >
        {icon ? (
          <ListItemIcon style={!drawerOpen ? { minWidth: 'auto', marginRight: 0 } : undefined}>
            {badge ? <Badge {...badge}>{icon}</Badge> : icon}
          </ListItemIcon>
        ) : null}
        {drawerOpen && <ListItemText primary={primary} />}
      </ListItem>
    </li>
  );
}

// ✅ COMPONENTE PRINCIPAL DE ACORDEÓN DE MENÚ
const AccordionMenu = ({ drawerClose, drawerOpen = true }) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const searchRef = useRef(null);
  const [menuQuery, setMenuQuery] = useState("");
  const [searchReady, setSearchReady] = useState(false);
  // const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // ✅ No utilizado por ahora
  
  const { user } = useContext(AuthContext);
  const { connectionWarning } = useContext(WhatsAppsContext);
  const { getPlanCompany } = usePlans();
  
  // ✅ ESTADO DE EXPANSIÓN DE GRUPOS
  const [expandedGroups, setExpandedGroups] = useState({
    whatsapp: false,
    dashboard: false,
    negocio: false,
    gruposWA: false,
    estadosWA: false,
    mensajes: false,
    configuracion: false,
    flowbuilder: false,
    utilidades: false,
  });
  const [planConfigVersion, setPlanConfigVersion] = useState(0);
  const { done: stepsDone, total: stepsTotal, isAdmin } = useGettingStarted({ includeExtras: false });
  const [badge, setBadge] = useState(readGettingStartedBadge);

  useEffect(() => {
    setBadge({ done: stepsDone, total: stepsTotal });
  }, [stepsDone, stepsTotal]);

  useEffect(() => {
    const onBadge = (event) => {
      if (event?.detail) setBadge(event.detail);
      else setBadge(readGettingStartedBadge());
    };
    window.addEventListener(GETTING_STARTED_EVENT, onBadge);
    return () => window.removeEventListener(GETTING_STARTED_EVENT, onBadge);
  }, []);

  useEffect(() => {
    const onKey = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ✅ CARGAR CONFIGURACIÓN DEL PLAN DIRECTAMENTE (ya que MainListItems puede no estar renderizado)
  useEffect(() => {
    let cancelled = false;

    async function fetchPlanConfig() {
      if (!user?.companyId) return;

      try {
        const planConfigs = await getPlanCompany(undefined, user.companyId);
        if (cancelled || !planConfigs?.plan) return;

        localStorage.setItem('showCampaigns', planConfigs.plan.useCampaigns ? 'true' : 'false');
        localStorage.setItem('showKanban', planConfigs.plan.useKanban ? 'true' : 'false');
        localStorage.setItem('showOpenAi', planConfigs.plan.useOpenAi ? 'true' : 'false');
        localStorage.setItem('showIntegrations', planConfigs.plan.useIntegrations ? 'true' : 'false');
        localStorage.setItem('showSchedules', planConfigs.plan.useSchedules ? 'true' : 'false');
        localStorage.setItem('showInternalChat', planConfigs.plan.useInternalChat ? 'true' : 'false');
        localStorage.setItem('showExternalApi', planConfigs.plan.useExternalApi ? 'true' : 'false');
        localStorage.setItem('showFiles', planConfigs.plan.useFiles ? 'true' : 'false');
        localStorage.setItem('showNegocio', planConfigs.plan.useNegocio ? 'true' : 'false');
        localStorage.setItem('showGroupCampaigns', planConfigs.plan.useGroupCampaigns == null || planConfigs.plan.useGroupCampaigns ? 'true' : 'false');
        localStorage.setItem('showStatusCampaigns', planConfigs.plan.useStatusCampaigns == null || planConfigs.plan.useStatusCampaigns ? 'true' : 'false');

        try {
          const { data } = await api.get("/users/can-access-agenda");
          if (!cancelled) {
            localStorage.setItem('canAccessAgenda', data?.canAccessAgenda ? 'true' : 'false');
          }
        } catch (agendaErr) {
          if (!cancelled) {
            localStorage.setItem('canAccessAgenda', 'false');
          }
        }

        if (!cancelled) {
          setPlanConfigVersion(prev => prev + 1);
        }
      } catch (error) {
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          return;
        }
        console.error('Error cargando configuración del plan:', error);
      }
    }

    fetchPlanConfig();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.companyId]);

  useEffect(() => {
    const next = {};
    Object.values(menuGroupsConfig).forEach((group) => {
      const active = (group.items || []).some((item) => {
        if (!item.route) return false;
        if (item.exact) return location.pathname === item.route;
        return (
          location.pathname === item.route ||
          (item.route !== "/" && location.pathname.startsWith(`${item.route}/`))
        );
      });
      if (active) next[group.id] = true;
    });
    if (Object.keys(next).length) {
      setExpandedGroups((prev) => ({ ...prev, ...next }));
    }
  }, [location.pathname]);

  // ✅ ESCUCHAR CAMBIOS EN LA CONFIGURACIÓN DEL PLAN PARA RE-RENDERIZAR
  useEffect(() => {
    // ✅ Escuchar evento personalizado cuando MainListItems actualiza la configuración (por si acaso)
    const handlePlanConfigUpdate = (event) => {
      setPlanConfigVersion(prev => prev + 1);
    };

    // ✅ Escuchar evento personalizado
    window.addEventListener('planConfigUpdated', handlePlanConfigUpdate);
    
    // ✅ También escuchar cambios en localStorage (para compatibilidad con otras pestañas)
    const handleStorageChange = (e) => {
      if (e.key && e.key.startsWith('show')) {
        setPlanConfigVersion(prev => prev + 1);
      }
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('planConfigUpdated', handlePlanConfigUpdate);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // ✅ FUNCIÓN PARA TOGGLE DE GRUPOS O NAVEGAR SI TIENE RUTA
  const handleGroupToggle = (groupId) => {
    const group = menuGroupsConfig[groupId];
    
    // ✅ Si el grupo tiene una ruta directa y no tiene items, navegar directamente
    if (group.route && (!group.items || group.items.length === 0)) {
      history.push(group.route);
      if (drawerClose) drawerClose();
      return;
    }
    
    // ✅ Si tiene items, expandir/colapsar como antes
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  // ✅ FUNCIÓN PARA VERIFICAR CONDICIONALES
  const checkConditional = (conditional, item) => {
    if (!conditional) return true;
    
    let result = false;
    switch (conditional) {
      case 'showKanban':
        result = localStorage.getItem('showKanban') === 'true';
        break;
      case 'showSchedules':
        result = localStorage.getItem('showSchedules') === 'true';
        break;
      case 'showInternalChat':
        result = localStorage.getItem('showInternalChat') === 'true';
        break;
      case 'showCampaigns':
        result = localStorage.getItem('showCampaigns') === 'true';
        break;
      case 'showOpenAi':
        result = localStorage.getItem('showOpenAi') === 'true';
        break;
      case 'showIntegrations':
        result = localStorage.getItem('showIntegrations') === 'true';
        break;
      case 'showExternalApi':
        result = localStorage.getItem('showExternalApi') === 'true';
        break;
      case 'showFiles':
        result = localStorage.getItem('showFiles') === 'true';
        break;
      case 'showGroupCampaigns':
        result = localStorage.getItem('showGroupCampaigns') !== 'false';
        break;
      case 'showStatusCampaigns':
        result = localStorage.getItem('showStatusCampaigns') !== 'false';
        break;
      case 'showNegocio': {
        const isSuperUser =
          user &&
          (user.profile === "super" || user.super === true || user.super === 1 || String(user.super) === "1");
        result =
          isSuperUser ||
          (user?.profile === "admin" && localStorage.getItem('showNegocio') === 'true');
        break;
      }
      case 'canAccessAgenda':
        result = localStorage.getItem('canAccessAgenda') === 'true';
        break;
      case 'user.super':
        result =
          user &&
          (user.profile === "super" ||
            user.super === true ||
            user.super === 1 ||
            String(user.super) === "1");
        break;
      case 'user.superOrCompanyAdmin':
        result =
          user &&
          (user.super === true ||
            user.super === 1 ||
            String(user.super) === "1" ||
            user.profile === "admin");
        break;
      default:
        result = true;
    }
    
    return result;
  };

  // ✅ FUNCIÓN PARA CREAR BADGE SI ES NECESARIO
  const createBadge = (item) => {
    if (item.route === '/connections' && connectionWarning) {
      return { badgeContent: "!", color: "error" };
    }
    if (item.route === '/chats') {
      return { color: "secondary", variant: "dot", invisible: false };
    }
    return null;
  };

  // ✅ RENDERIZADO DE GRUPO DE MENÚ
  const renderMenuGroup = (groupId) => {
    const group = menuGroupsConfig[groupId];
    if (!group) return null;
    if (group.conditional && !checkConditional(group.conditional, group)) {
      return null;
    }

    // ✅ VERIFICAR PERMISOS A NIVEL DE GRUPO
    // ✅ Solo el grupo "whatsapp" (CHATS) es visible para usuarios regulares
    // ✅ Todos los demás grupos (dashboard, gruposWA, mensajes, configuracion, utilidades) son solo para admins
    const isAdminGroup = groupId !== "whatsapp" && groupId !== "dashboard";
    const isElevated =
      user?.profile === "admin" ||
      user?.profile === "super" ||
      user?.super === true ||
      user?.super === 1 ||
      String(user?.super) === "1";
    if (isAdminGroup && !isElevated) {
      return null;
    }
    if (groupId === "dashboard" && !check(user?.profile, "dashboard:view")) {
      return null;
    }

    const query = menuQuery.trim().toLowerCase();
    const filteredItems = (group.items || []).filter((item) => {
      if (!checkConditional(item.conditional, item)) return false;
      if (item.kind === "section") return !query;
      if (!check(user?.profile, item.permission)) return false;
      if (!query) return true;
      const label = i18n.t(item.translationKey) || item.name || "";
      const groupTitle = i18n.t(group.titleKey) || "";
      return `${label} ${groupTitle} ${item.route}`.toLowerCase().includes(query);
    });
    const visibleItems = filteredItems.filter((item, index) => {
      if (item.kind !== "section") return true;
      const children = [];
      for (const next of filteredItems.slice(index + 1)) {
        if (next.kind === "section") break;
        children.push(next);
      }
      return children.length > 0;
    });
    const hasVisibleItems = visibleItems.length > 0;
    if (query && !hasVisibleItems && !group.route) return null;

    const isExpanded = Boolean(query) || expandedGroups[groupId];
    const isItemActive = (item) => {
      if (!item?.route) return false;
      if (item.exact) return location.pathname === item.route;
      return location.pathname === item.route || (item.route !== "/" && location.pathname.startsWith(`${item.route}/`));
    };
    const isGroupActive = Boolean(visibleItems.some(isItemActive));

    // ✅ Si el grupo tiene ruta directa y no tiene items visibles, mostrar solo el header clickeable
    if (group.route && !hasVisibleItems) {
      return (
        <React.Fragment key={groupId}>
          <ListItem
            button
            component={RouterLink}
            to={group.route}
            onClick={drawerClose}
            className={clsx(classes.groupHeader, isGroupActive && classes.groupHeaderActive, !drawerOpen && classes.groupHeaderCollapsed)}
            style={{
              '--group-color': group.color,
              paddingLeft: drawerOpen ? undefined : '4px',
              paddingRight: drawerOpen ? undefined : '4px',
              justifyContent: drawerOpen ? 'flex-start' : 'center',
              margin: drawerOpen ? undefined : '4px 4px',
            }}
          >
            <ListItemIcon style={{ minWidth: drawerOpen ? '40px' : 'auto', marginRight: drawerOpen ? undefined : 0 }}>
              <span className={classes.groupIcon}>{group.icon}</span>
            </ListItemIcon>
            {drawerOpen && (
              <ListItemText
                primary={
                  <Typography className={classes.groupTitle}>
                    {i18n.t(group.titleKey)}
                  </Typography>
                }
              />
            )}
          </ListItem>
        </React.Fragment>
      );
    }

    if (!hasVisibleItems && !group.route) return null;

    return (
      <React.Fragment key={groupId}>
        {/* ✅ HEADER DEL GRUPO */}
        <ListItem
          button
          onClick={() => handleGroupToggle(groupId)}
          className={clsx(
            classes.groupHeader,
            isGroupActive && classes.groupHeaderActive,
            isExpanded && classes.groupHeaderExpanded,
            !drawerOpen && classes.groupHeaderCollapsed
          )}
          style={{
            '--group-color': group.color,
            paddingLeft: drawerOpen ? undefined : '4px',
            paddingRight: drawerOpen ? undefined : '4px',
            justifyContent: drawerOpen ? 'flex-start' : 'center',
            margin: drawerOpen ? undefined : '4px 4px',
          }}
        >
          <ListItemIcon style={{ minWidth: drawerOpen ? '40px' : 'auto', marginRight: drawerOpen ? undefined : 0 }}>
            <span className={classes.groupIcon}>{group.icon}</span>
          </ListItemIcon>
          {drawerOpen && (
            <>
              <ListItemText
                primary={
                  <Typography className={classes.groupTitle}>
                    {i18n.t(group.titleKey)}
                  </Typography>
                }
              />
              {isExpanded ? (
                <ExpandLessIcon className={`${classes.expandIcon} ${classes.expandedIcon}`} />
              ) : (
                <ExpandMoreIcon className={classes.expandIcon} />
              )}
            </>
          )}
        </ListItem>

        {/* ✅ ELEMENTOS DEL GRUPO */}
        {hasVisibleItems && drawerOpen && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <div className={classes.groupItemsWrap}>
              <List component="div" disablePadding>
                {visibleItems.map((item, index) => {
                  if (item.kind === "section") {
                    return (
                      <Typography key={`${groupId}-section-${index}`} className={classes.nestedSectionLabel}>
                        {i18n.t(item.translationKey)}
                      </Typography>
                    );
                  }
                  return (
                    <Can
                      key={`${groupId}-${index}`}
                      role={user.profile}
                      perform={item.permission}
                      yes={() => (
                        <ListItemLink
                          to={item.route}
                          primary={i18n.t(item.translationKey)}
                          icon={group.renderIcon(item.icon)}
                          className={classes.nestedItem}
                          badge={createBadge(item)}
                          onClick={drawerClose}
                          drawerOpen={drawerOpen}
                          classes={classes}
                          selected={isItemActive(item)}
                        />
                      )}
                    />
                  );
                })}
              </List>
            </div>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  return (
    <>
      {drawerOpen && (
        <Box className={classes.searchWrap}>
          <SearchIcon className={classes.searchIcon} />
          <InputBase
            inputRef={searchRef}
            className={classes.searchInput}
            placeholder="Buscar sección"
            value={menuQuery}
            onChange={(event) => setMenuQuery(event.target.value)}
            onFocus={() => setSearchReady(true)}
            readOnly={!searchReady}
            inputProps={{
              autoComplete: "off",
              autoCorrect: "off",
              autoCapitalize: "off",
              spellCheck: false,
              name: "menu-section-filter",
              "data-lpignore": "true",
              "data-1p-ignore": "true",
              "data-form-type": "other",
            }}
          />
          {menuQuery ? (
            <button type="button" className={classes.searchClear} aria-label="Borrar búsqueda" onClick={() => setMenuQuery("")}>
              ×
            </button>
          ) : (
            <span className={classes.searchKbd}>Ctrl K</span>
          )}
        </Box>
      )}
      {drawerOpen && menuQuery.trim() && (
        <div className={classes.searchEmpty}>
          El menú solo muestra secciones que coinciden con este texto.
        </div>
      )}
      {(!menuQuery.trim() ||
        `${i18n.t("mainDrawer.listItems.gettingStarted")} primeros pasos getting-started`
          .toLowerCase()
          .includes(menuQuery.trim().toLowerCase())) && (
        <ListItem
          button
          component={RouterLink}
          to="/getting-started"
          onClick={drawerClose}
          selected={location.pathname === "/getting-started"}
          className={clsx(classes.firstStepsItem, !drawerOpen && classes.groupHeaderCollapsed)}
        >
          <ListItemIcon>
            <FaRocket size={18} />
          </ListItemIcon>
          {drawerOpen && (
            <>
              <ListItemText primary={i18n.t("mainDrawer.listItems.gettingStarted")} />
              {isAdmin && badge.total > 0 && (
                <span className={classes.stepsBadge}>
                  {badge.done}/{badge.total}
                </span>
              )}
            </>
          )}
        </ListItem>
      )}
      {drawerOpen &&
        (!menuQuery.trim() ||
          `${i18n.t("mainDrawer.listItems.gettingStarted")} primeros pasos getting-started`
            .toLowerCase()
            .includes(menuQuery.trim().toLowerCase())) && (
          <div className={classes.menuSectionDivider} />
        )}
      {Object.keys(menuGroupsConfig).map(renderMenuGroup)}
    </>
  );
};

export default AccordionMenu;
